import React from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { alpha, useTheme } from "@mui/material/styles";
import { composerPanelBorderSx } from "./composerBorderStyles";

export type EventSettingCardProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  /** Optional info tooltip beside the title. */
  titleTooltip?: string;
  control: React.ReactNode;
  children?: React.ReactNode;
  error?: string;
};

const EventSettingCard: React.FC<EventSettingCardProps> = ({
  icon,
  title,
  description,
  titleTooltip,
  control,
  children,
  error,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        ...composerPanelBorderSx,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.5,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: "primary.main",
          }}
        >
          {icon}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="subtitle1" fontWeight={400} lineHeight={1.3}>
              {title}
            </Typography>
            {titleTooltip ? (
              <Tooltip title={titleTooltip} arrow>
                <IconButton
                  size="small"
                  aria-label={titleTooltip}
                  sx={{
                    p: 0.25,
                    color: "text.secondary",
                    "&:hover": { color: "primary.main" },
                  }}
                  onClick={(event) => event.stopPropagation()}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.25, lineHeight: 1.35 }}
          >
            {description}
          </Typography>
        </Box>

        <Box sx={{ flexShrink: 0, maxWidth: { xs: "46%", sm: "42%" } }}>
          {control}
        </Box>
      </Box>

      {children ? (
        <Box
          sx={{
            px: 2,
            pb: 2,
            pt: 0,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: alpha(theme.palette.primary.main, 0.02),
          }}
        >
          {children}
        </Box>
      ) : null}

      {error ? (
        <Typography
          variant="caption"
          color="error"
          sx={{ display: "block", px: 2, pb: 1.25, mt: children ? 0 : -0.5 }}
        >
          {error}
        </Typography>
      ) : null}
    </Box>
  );
};

export default EventSettingCard;
